/**
 * frontend/Settings.jsx
 * SoundDAO — User settings page at /settings.
 *
 * Currently: wallet linking for magic-link users.
 * Expanding in Milestone 5 with notification prefs, rank display, etc.
 */

import React, { useState } from 'react';
import { supabase } from './lib/supabaseClient';
import { authFetch } from './lib/authFetch';
import { linkWalletWithSignature } from './lib/walletLink';

import './Settings.css';

// The separate static claim site (sounddao-contracts/claim, its own Vercel project).
const CLAIM_URL = process.env.REACT_APP_CLAIM_URL || 'https://sounddao-claim.vercel.app';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function Settings({ session, user, onUserUpdated, onBack }) {
  const [walletStatus, setWalletStatus] = useState(null);  // null | 'connecting' | 'linked' | 'error'
  const [walletError,  setWalletError]  = useState(null);
  const [linkedAddress, setLinkedAddress] = useState(user?.wallet || null);
  const [muted, setMuted] = useState(localStorage.getItem('sounddao_mute_sfx') === 'true');

  // Display name state
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [savingName,  setSavingName]  = useState(false);
  const [nameError,   setNameError]   = useState('');
  const [nameSaved,   setNameSaved]   = useState(false);

  const truncate = (addr) => addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : '';

  // ── Connect wallet and link to user account ────────────────────────────────
  const handleConnectWallet = async () => {
    setWalletError(null);

    if (!window.ethereum) {
      setWalletError('No wallet detected. Please install MetaMask.');
      return;
    }

    try {
      setWalletStatus('connecting');
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const address  = accounts[0];

      // Prompt 1.5: sign a single-use server nonce; the backend verifies the
      // signer before linking. (PATCH /api/users/:id no longer accepts wallet.)
      const linked = await linkWalletWithSignature(address);

      setLinkedAddress(linked);
      setWalletStatus('linked');
      if (onUserUpdated) onUserUpdated();

    } catch (err) {
      if (err.code === 4001) {
        setWalletError('Wallet connection cancelled.');
      } else {
        setWalletError(err.message || 'Failed to link wallet. Please try again.');
      }
      setWalletStatus('error');
    }
  };

  // ── Save display name ─────────────────────────────────────────────────────
  const handleSaveDisplayName = async () => {
    setSavingName(true);
    setNameError('');
    try {
      const res = await authFetch(`${API_BASE}/api/users/${session.user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: displayName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setNameError(data.error || 'Failed to save.');
      } else {
        setNameSaved(true);
        if (onUserUpdated) onUserUpdated(data.user);
      }
    } catch {
      setNameError('Something went wrong. Try again.');
    } finally {
      setSavingName(false);
    }
  };

  // ── Sign out ───────────────────────────────────────────────────────────────
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    // App will re-render to AuthScreen via useAuth session listener
  };

  return (
    <div className="settings-screen">
      <div className="settings-header">
        <button className="settings-back" onClick={onBack}>← Back</button>
        <h1 className="settings-title">Settings</h1>
      </div>

      <div className="settings-body">

        {/* ── Profile ── */}
        <section className="settings-section">
          <h2 className="settings-section-title">Profile</h2>

          {/* Display name — editable */}
          <div className="settings-field">
            <label className="settings-label">Display name</label>
            <div className="settings-input-row">
              <input
                type="text"
                value={displayName}
                onChange={e => {
                  setDisplayName(e.target.value);
                  setNameSaved(false);
                  setNameError('');
                }}
                placeholder="How you appear on the leaderboard"
                maxLength={30}
                className="settings-input"
              />
              <button
                onClick={handleSaveDisplayName}
                disabled={savingName || !displayName.trim()}
                className="settings-btn"
              >
                {savingName ? 'Saving…' : nameSaved ? 'Saved ✓' : 'Save'}
              </button>
            </div>
            {nameError && <p className="settings-error">{nameError}</p>}
            <p className="settings-hint">
              Max 30 characters. Shown on leaderboard and to creators.
            </p>
          </div>

          <div className="settings-row">
            <span className="settings-label">Email</span>
            <span className="settings-value">{session?.user?.email || '—'}</span>
          </div>
          <div className="settings-row">
            <span className="settings-label">Persona</span>
            <span className="settings-value settings-value--capitalize">{user?.persona || '—'}</span>
          </div>
          <div className="settings-row">
            <span className="settings-label">Rank</span>
            <span className="settings-value">{user?.rank_title || 'Street Team'}</span>
          </div>
          <div className="settings-row">
            <span className="settings-label">Referral code</span>
            <span className="settings-value settings-value--code">{user?.referral_code || '—'}</span>
          </div>
          <div className="settings-row">
            <span className="settings-label">Reward sound effect</span>
            <label className="toggle">
              <input
                type="checkbox"
                checked={!muted}
                onChange={(e) => {
                  const mute = !e.target.checked;
                  localStorage.setItem('sounddao_mute_sfx', mute ? 'true' : 'false');
                  setMuted(mute);
                }}
              />
              <span className="toggle-slider" />
            </label>
          </div>
        </section>

        {/* ── Wallet ── */}
        <section className="settings-section">
          <h2 className="settings-section-title">Wallet</h2>
          <p className="settings-section-desc">
            Link your wallet to receive testnet SOUND: a linked, signed address is added to the next
            daily claim tree.
          </p>

          {(linkedAddress || walletStatus === 'linked') ? (
            <div className="settings-wallet-linked">
              <span className="settings-wallet-dot" />
              <span className="settings-wallet-address">{truncate(linkedAddress)}</span>
              <span className="settings-wallet-badge">Connected</span>
            </div>
          ) : (
            <>
              <button
                className="settings-btn-wallet"
                onClick={handleConnectWallet}
                disabled={walletStatus === 'connecting'}
              >
                {walletStatus === 'connecting' ? 'Connecting…' : '🦊 Connect wallet'}
              </button>
              {walletError && <p className="settings-error">{walletError}</p>}
            </>
          )}
          <a className="settings-claim-link" href={CLAIM_URL} target="_blank" rel="noopener noreferrer">
            Claim SOUND (testnet) ↗
          </a>
        </section>

        {/* ── Account ── */}
        <section className="settings-section">
          <h2 className="settings-section-title">Account</h2>
          <button className="settings-btn-danger" onClick={handleSignOut}>
            Sign out
          </button>
        </section>

      </div>
    </div>
  );
}
